import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Plus,
  QrCode,
  Trash2,
  Download,
  Settings,
  Users as UsersIcon,
  CheckSquare,
  Square,
  Trash,
  X,
  Search,
  Filter,
} from 'lucide-react';
import PaginationControls from '../../components/admin/PaginationControls';

const Users = () => {
  const [wgPeers, setWgPeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPeers, setSelectedPeers] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    count: 1,
  });

  useEffect(() => {
    loadWgPeers();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, pageSize]);

  const loadWgPeers = async () => {
    try {
      const response = await adminAPI.getWgPeers();
      setWgPeers(response.data);
    } catch (error) {
      console.error('Failed to load WG peers:', error);
      toast.error(error.response?.data?.error || 'Failed to load WireGuard peers');
    } finally {
      setLoading(false);
    }
  };

  const filteredPeers = useMemo(() => {
    return wgPeers.filter((peer) => {
      const matchesSearch = !searchTerm || [
        peer.name,
        peer.description,
        peer.ip_cidr,
        peer.pubkey,
      ].some((value) => String(value || '').toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = statusFilter === 'all' || (peer.status || 'unknown') === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [wgPeers, searchTerm, statusFilter]);

  const paginatedPeers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredPeers.slice(start, start + pageSize);
  }, [filteredPeers, page, pageSize]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await adminAPI.createWgPeer(formData);
      const count = formData.count || 1;
      toast.success(`${count} WireGuard peer(s) created successfully`);
      setShowModal(false);
      resetForm();
      loadWgPeers();
    } catch (error) {
      console.error('Failed to create WG peer:', error);
      toast.error(error.response?.data?.error || 'Failed to create WireGuard peer');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      count: 1,
    });
  };

  const generateWGConfig = async (peerId, peerName) => {
    try {
      await adminAPI.generateWGConfig(peerId);

      const downloadFiles = async () => {
        try {
          const authHeaders = {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          };

          const qrResponse = await fetch(`/api/download/qr/${peerId}`, { headers: authHeaders });
          if (qrResponse.ok) {
            const blob = await qrResponse.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `${peerName}-wireguard-qr.png`;
            link.click();
            window.URL.revokeObjectURL(downloadUrl);
          }

          const confResponse = await fetch(`/api/download/config/${peerId}`, { headers: authHeaders });
          if (confResponse.ok) {
            const blob = await confResponse.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `${peerName}-wireguard.conf`;
            link.click();
            window.URL.revokeObjectURL(downloadUrl);
          }

          toast.success(`WireGuard files downloaded for ${peerName}`);
        } catch (error) {
          console.error('Download failed:', error);
          toast.error('Failed to download WireGuard files');
        }
      };

      await downloadFiles();
    } catch (error) {
      console.error('Failed to generate WG config:', error);
      toast.error(error.response?.data?.error || 'Failed to generate WireGuard config');
    }
  };

  const deletePeer = async (peerId, peerName) => {
    if (!window.confirm(`Are you sure you want to delete WireGuard peer "${peerName}"?`)) {
      return;
    }

    try {
      await adminAPI.deleteWgPeer(peerId);
      toast.success(`WireGuard peer "${peerName}" deleted successfully`);
      loadWgPeers();
    } catch (error) {
      console.error('Failed to delete WG peer:', error);
      toast.error(error.response?.data?.error || `Failed to delete peer "${peerName}"`);
    }
  };

  const togglePeerSelection = (peerId) => {
    const newSelected = new Set(selectedPeers);
    if (newSelected.has(peerId)) {
      newSelected.delete(peerId);
    } else {
      newSelected.add(peerId);
    }
    setSelectedPeers(newSelected);
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedPeers.map((peer) => peer.id);
    const allSelected = pageIds.every((peerId) => selectedPeers.has(peerId));
    const nextSelected = new Set(selectedPeers);

    if (allSelected) {
      pageIds.forEach((peerId) => nextSelected.delete(peerId));
    } else {
      pageIds.forEach((peerId) => nextSelected.add(peerId));
    }

    setSelectedPeers(nextSelected);
  };

  const bulkDeletePeers = async () => {
    if (selectedPeers.size === 0) {
      toast.error('Please select peers to delete');
      return;
    }

    const selectedPeerDetails = wgPeers.filter((peer) => selectedPeers.has(peer.id));

    if (!window.confirm(`Are you sure you want to delete ${selectedPeerDetails.length} selected peer(s)?`)) {
      return;
    }

    try {
      const results = await Promise.all(
        selectedPeerDetails.map(async (peer) => {
          try {
            await adminAPI.deleteWgPeer(peer.id);
            return { success: true };
          } catch (error) {
            return { success: false, error };
          }
        })
      );

      const failedCount = results.filter((result) => !result.success).length;
      const successCount = results.length - failedCount;

      if (successCount > 0) {
        toast.success(`${successCount} peer(s) deleted successfully`);
      }
      if (failedCount > 0) {
        toast.error(`Failed to delete ${failedCount} peer(s)`);
      }

      setSelectedPeers(new Set());
      loadWgPeers();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Failed to delete peers');
    }
  };

  const deleteAllPeers = async () => {
    if (filteredPeers.length === 0) {
      toast.error('No peers to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${filteredPeers.length} peer(s) in the current result set?`)) {
      return;
    }

    try {
      await Promise.all(filteredPeers.map((peer) => adminAPI.deleteWgPeer(peer.id)));
      toast.success('Peers deleted successfully');
      setSelectedPeers(new Set());
      loadWgPeers();
    } catch (error) {
      console.error('Delete all failed:', error);
      toast.error(error.response?.data?.error || 'Failed to delete all peers');
    }
  };

  const getStatusClass = (status) => {
    if (status === 'active') return 'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-green-50 text-green-700';
    if (status === 'disabled') return 'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-yellow-50 text-yellow-700';
    return 'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-gray-50 text-gray-700';
  };

  return (
    <div className="p-8">
      <div className="bg-white border-b border-gray-200 px-8 py-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <UsersIcon className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">WireGuard Peers</h1>
              <p className="text-sm text-gray-600 mt-1">
                Generate, download, and retire peer configurations in a cleaner workflow built for desktop and mobile.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {paginatedPeers.length > 0 ? (
              <>
                <button type="button" onClick={toggleSelectAll} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                  <CheckSquare className="h-4 w-4" />
                  <span>Select Page</span>
                </button>

                {selectedPeers.size > 0 ? (
                  <button type="button" onClick={bulkDeletePeers} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700">
                    <Trash2 className="h-4 w-4" />
                    <span>Delete ({selectedPeers.size})</span>
                  </button>
                ) : null}

                <button type="button" onClick={deleteAllPeers} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700">
                  <Trash className="h-4 w-4" />
                  <span>Delete Filtered</span>
                </button>
              </>
            ) : null}

            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-md hover:bg-gray-800"
            >
              <Plus className="h-4 w-4" />
              <span>Generate Peer</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Settings className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-blue-900">Setup Guide</h2>
            <p className="text-sm text-blue-700 mt-1">The peer workflow and server actions are unchanged. Only the interface has been refreshed.</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search name, description, IP, or key"
              className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none">
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-gray-600 mt-4">Loading WireGuard peers...</p>
        </div>
      ) : filteredPeers.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-gray-100 rounded-lg">
              <UsersIcon className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{wgPeers.length === 0 ? 'No WireGuard peers yet' : 'No peers match your filters'}</h3>
          <p className="text-gray-600 mb-6">
            {wgPeers.length === 0
              ? 'Generate your first peer to start issuing QR codes and configuration files.'
              : 'Adjust the search or filter settings to see more peers.'}
          </p>
          {wgPeers.length === 0 ? (
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-md hover:bg-gray-800"
            >
              <Plus className="h-4 w-4" />
              <span>Generate First Peer</span>
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {paginatedPeers.map((peer) => (
              <div key={peer.id} className="bg-white rounded-lg border border-gray-200 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => togglePeerSelection(peer.id)}
                      className="p-1 rounded hover:bg-gray-50 shrink-0 mt-1"
                      title="Select peer"
                    >
                      {selectedPeers.has(peer.id) ? <CheckSquare className="h-4 w-4 text-gray-900" /> : <Square className="h-4 w-4 text-gray-400" />}
                    </button>

                    <div className="p-2 bg-gray-100 rounded-md shrink-0">
                      <span className="text-sm font-semibold text-gray-700">{peer.name ? peer.name.charAt(0).toUpperCase() : 'P'}</span>
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-gray-900 truncate">{peer.name || 'Unnamed Peer'}</h3>
                        <span className={getStatusClass(peer.status || 'unknown')}>{peer.status || 'unknown'}</span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{peer.description || 'Mobile device iPhone 14'}</p>
                      
                      <div className="flex items-center gap-6 mt-3 text-sm">
                        <div>
                          <span className="text-gray-500">VPN IP</span>
                          <p className="font-mono text-gray-900">{peer.ip_cidr || 'Not assigned'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">CREATED</span>
                          <p className="text-gray-900">
                            {peer.created_at ? new Date(peer.created_at).toLocaleDateString() : 'Unknown'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">FILES</span>
                          <p className="text-gray-900">QR and config download available</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => generateWGConfig(peer.id, peer.name || 'Unnamed Peer')}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      title="Download WireGuard Files"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => deletePeer(peer.id, peer.name || 'Unnamed Peer')}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                      title="Delete Peer"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <PaginationControls
            page={page}
            pageSize={pageSize}
            totalItems={filteredPeers.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            itemLabel="peers"
          />
        </>
      )}

      {showModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <QrCode className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Generate WireGuard Peer{formData.count > 1 ? 's' : ''}
                    </h2>
                    <p className="text-sm text-gray-500">Peer generation behavior stays the same. This is a UI refresh only.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Peer Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                    placeholder="e.g. Office Phone"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                    placeholder="Additional notes about this peer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Peers</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={formData.count}
                    onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value, 10) || 1 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                    placeholder="1"
                  />
                  <p className="text-sm text-gray-600 mt-1">Create up to 50 peers at once. Names are numbered automatically.</p>
                </div>

                <div className="flex flex-col gap-2 pt-4 sm:flex-row">
                  <button type="submit" className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 transition flex items-center justify-center">
                    <QrCode className="h-4 w-4 mr-2" />
                    <span>{formData.count > 1 ? `Generate ${formData.count} Peers` : 'Generate Peer'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Users;
